import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { AuthUser } from '@chronos/shared';
import { useAuth } from '@/auth/AuthContext';
import { apiFetch } from '@/api/client';
import { Card, ErrorNote, PageHeader, SubmitButton } from '@/components/ui';

/**
 * The person's own record. Personal details are theirs to maintain; name,
 * email, username and roles are admin-set and shown read-only, so people can
 * at least see what is stored about them and ask for a correction.
 */
export function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({
    dateOfBirth: user?.personal?.dateOfBirth ?? '',
    anniversaryDate: user?.personal?.anniversaryDate ?? '',
    phone: user?.personal?.phone ?? '',
    address: user?.personal?.address ?? '',
  });
  const [saved, setSaved] = useState(false);

  const set = (patch: Partial<typeof form>) => {
    setForm((f) => ({ ...f, ...patch }));
    setSaved(false);
  };

  const save = useMutation({
    mutationFn: () =>
      apiFetch<AuthUser>('/api/users/me/details', {
        method: 'PATCH',
        // An empty input means "clear it", which the API expresses as null.
        body: {
          dateOfBirth: form.dateOfBirth || null,
          anniversaryDate: form.anniversaryDate || null,
          phone: form.phone.trim() || null,
          address: form.address.trim() || null,
        },
      }),
    onSuccess: async () => {
      setSaved(true);
      await refreshUser();
    },
  });

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader title="My profile" description="What TickTock stores about you. Ask an admin to change anything you cannot edit here." />

      <Card className="mt-4">
        <div className="border-b border-slate-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-slate-800">Account</h2>
          <p className="text-xs text-slate-500">Set by an admin.</p>
        </div>
        <dl className="grid gap-3 px-4 py-3 sm:grid-cols-2">
          <div><dt className="text-xs text-slate-500">Name</dt><dd className="text-sm text-slate-800">{user?.name}</dd></div>
          <div><dt className="text-xs text-slate-500">Username</dt><dd className="text-sm text-slate-800">{user?.username}</dd></div>
          <div><dt className="text-xs text-slate-500">Email</dt><dd className="text-sm text-slate-800">{user?.email}</dd></div>
          <div><dt className="text-xs text-slate-500">Roles</dt><dd className="text-sm text-slate-800">{user?.roles.join(', ')}</dd></div>
        </dl>
      </Card>

      <Card className="mt-4">
        <div className="border-b border-slate-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-slate-800">Personal details</h2>
          <p className="text-xs text-slate-500">Yours to keep up to date. Only you and an admin can see these.</p>
        </div>
        <form
          className="space-y-4 px-4 py-3"
          onSubmit={(e) => {
            e.preventDefault();
            setSaved(false);
            save.mutate();
          }}
        >
          <ErrorNote error={save.error} />
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="label" htmlFor="p-dob">Date of birth</label>
              <input id="p-dob" type="date" className="input" value={form.dateOfBirth} onChange={(e) => set({ dateOfBirth: e.target.value })} />
            </div>
            <div>
              <label className="label" htmlFor="p-anniv">Anniversary</label>
              <input id="p-anniv" type="date" className="input" value={form.anniversaryDate} onChange={(e) => set({ anniversaryDate: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="label" htmlFor="p-phone">Phone number</label>
            <input id="p-phone" type="tel" className="input" maxLength={40} placeholder="+91 98765 43210" value={form.phone} onChange={(e) => set({ phone: e.target.value })} />
          </div>
          <div>
            <label className="label" htmlFor="p-address">Address</label>
            <textarea id="p-address" className="input" rows={3} maxLength={500} value={form.address} onChange={(e) => set({ address: e.target.value })} />
          </div>
          <div className="flex items-center gap-3">
            <SubmitButton pending={save.isPending} className="btn-primary">Save</SubmitButton>
            {saved && <span className="text-xs text-emerald-700">Saved.</span>}
          </div>
        </form>
      </Card>
    </div>
  );
}
